import { ReactNode } from 'react'

import { FileApiType } from '@/api/shared/types'

type RequestTypeApi =
  | 'permanent_family'
  | 'permanent_owner'
  | 'permanent_staff'
  | 'permanent_renter'
  | 'permanent_auto'
  | 'courier'
  | 'guest'
  | 'auto'
  | 'property'

export type PassAuto = {
  id: string
  number: string
  model?: string | null
  parkingPlace: string | null
  isStandard: boolean
}

/** Авто в теле запроса на создание пропуска: id присваивает бэкенд */
export type PassAutoPayload = Omit<PassAuto, 'id'>

export type CreateServicePassDTO = {
  projectId: string
  typeAlias?: RequestTypeAlias
  premiseId?: string
  desiredDate?: string | null
  persons?: string[]
  autos?: PassAutoPayload[]
  properties?: string
  description?: string
  personalAccountId?: string
  isStandard?: boolean
}

export enum DesiredDateType {
  ANY = 'any',
  ASAP = 'asap',
  NONE = 'none',
  INTERVAL = 'interval'
}

export interface DesiredDateInterval {
  start: string
  end: string
}

export type CreateServiceRequestDTO = {
  projectId: string
  premiseId?: string
  description: string
  files?: File[]
  personalAccountId?: string
  desiredDateType?: DesiredDateType
  desiredDateInterval?: DesiredDateInterval
  'desiredDateInterval.start'?: string
  'desiredDateInterval.end'?: string
}

export type RequestPassApi = {
  projectId: string
  premiseId?: string
  desiredDate?: string
  request_type?: RequestTypeApi
  typeAlias?: RequestTypeAlias
  properties?: string
  persons: string[]
  autos: PassAuto[]
  description?: string
  personalAccountId?: string
}

export type RequestRating = {
  rating: number
  comment: string
  files: FileApiType[]
}

export type RequestByIdApiType = {
  id: string
  number: number
  createdAt: string
  statusAlias: RequestStatusAlias
  typeAlias: RequestTypeAlias
  premiseId: string
  title: string
  description: string | null
  isPayable: boolean
  serviceName: string | null
  invoiceId: string | null
  desiredDate?: string
  desiredDateStart?: string
  desiredDateEnd?: string
  rating: RequestRating
  persons: string[]
  autos: PassAuto[]
  properties: string[] | string
  files: FileApiType[]
  projectId: string
  personalAccountId: string
  unreadComments: number
  authorTitle?: string | null
  canBeRated: boolean
  canBePaid: boolean
  canBeRejected: boolean
  isButtonUnsolvedAvailable: boolean
  canAutoBeChanged: boolean
}

export type RequestByIdType = RequestByIdApiType & { status: RequestStatus }

export interface CreateRequestApi {
  id?: string
}

export enum RequestStatusAlias {
  All = 'ALL',
  New = 'NEW',
  Penging = 'PENDING',
  InWork = 'INWORK',
  Paused = 'PAUSED',
  Calculation = 'CALCULATION',
  PaymentPending = 'PAYMENT_PENDING',
  Paid = 'PAID',
  Control = 'CONTROL',
  Complete = 'COMPLETE',
  Reject = 'REJECT',
  Reopen = 'REOPEN'
}

export enum RequestTypeAlias {
  PermanentPassFamily = 'PERMANENT_PASS_FAMILY',
  PermanentPassStaff = 'PERMANENT_PASS_STAFF',
  PermanentPassRenter = 'PERMANENT_PASS_RENTER',
  PermanentPassAuto = 'PERMANENT_PASS_AUTO',
  CourierPass = 'COURIER_PASS',
  GuestPass = 'GUEST_PASS',
  GuestPassAuto = 'GUEST_PASS_AUTO',
  PropertiesPass = 'PROPERTIES_PASS',
  CommonRequest = 'COMMON_REQUEST',
  PermanentPass = 'PERMANENT_PASS',
  RentPassAuto = 'RENT_PASS_AUTO',
  Pass = 'pass'
}

export enum PremiseTypeAlias {
  Flat = 'FLAT',
  Apartment = 'APARTMENT',
  Office = 'OFFICE',
  CarSpace = 'CAR_SPACE'
}

export type RequestsListPayload = {
  projectId: string
  typeAlias?: string[]
  statusAlias?: string[]
  query?: string
  createdAt?: string | null
  premises?: string[]
  payable?: string[]
}

export type AutoResponse = {
  number: string
  model: string
  parkingPlace: string
}

export type RequestResponse = {
  id: string
  number: number
  createdAt: string
  statusAlias: RequestStatusAlias
  typeAlias: RequestTypeAlias
  premiseId: string
  title: string
  description?: string
  serviceName?: string
  desiredDate?: string
  desiredDateStart?: string
  desiredDateEnd?: string
  projectId: string
  personalAccountId: string
  unreadComments: number
  rating?: number
  persons: string[]
  autos: AutoResponse[]
  properties: string[] | null
  isPayable: boolean
}

export type RequestType = {
  guid: string
  id: number
  title: string
  description?: string
  typeAlias: RequestTypeAlias
  icon_link?: string
  status?: RequestStatus
  status_alias: string
  date: string
  desiredDateStart?: string
  desiredDateEnd?: string
  personal_account_guid: string
  pass_status?: string
  stars: number
  comments_count: number
  persons: string[] | null
  autos: AutoResponse[] | null
  properties: string[] | null
  premiseId: string
  isPayable: boolean
}

export type RequestTypeType = {
  id: string
  name: string
  alias: RequestTypeAlias
  iconLink: string
  isActive: boolean
  description: string
  isShownOnMain: boolean
  icon?: ReactNode
  isNew: boolean
}

export type RequestTypesResponse = {
  data: RequestTypeType[]
  arePassesAvailable: boolean
  areRequestsAvailable: boolean
}

export type RequestCounts = {
  activeRequestsCount: number
}

export type RequestStatus = {
  id: string
  name: string
  description: string
  alias: RequestStatusAlias
  isActive: boolean
  color: string
  iconUrl: string
}

export type RequestStatusResponse = {
  data: RequestStatus[]
}

export enum FileType {
  Remote = 'Remote',
  Local = 'Local'
}

export enum QuickFilterParameterName {
  StatusAlias = 'statusAlias',
  TypeAlias = 'typeAlias',
  Payable = 'payable'
}

export enum QuickFilterType {
  Pass = 'pass',
  Common = 'common'
}

export enum QuickFilterAlias {
  All = 'all',
  Active = 'active', // активные статусы
  Closed = 'closed', // завершенные статусы
  Payable = 'payable', // платные обращения
  Unpayable = 'unpayable', // бесплатные обращения
  Temporary = 'temporary', // временные пропуска
  Permanent = 'permanent' // постоянный пропуск
}

export type QuickFilterParameter = {
  name: QuickFilterParameterName
  data: string[]
}

export enum QuickFilterComponent {
  MainList = 'main_list',
  PermanentPassAuto = 'permanent_pass_auto',
  PermanentPassMen = 'permanent_pass_men'
}

export type RequestQuickFilters = {
  name: string
  type: QuickFilterType
  parameters: QuickFilterParameter[]
  alias: QuickFilterAlias
  component: QuickFilterComponent
}

export type RequestQuickFiltersResponse = {
  data: RequestQuickFilters[]
}

export type RatingValue = 1 | 2 | 3 | 4 | 5

export type RateParameters = {
  rating: RatingValue
  rateParameters: Array<string>
}

export type RateParametersResponse = {
  data: RateParameters[]
}

export type RequestFeedback = {
  rating: RatingValue | null
  rateParameters?: Array<string>
  comment?: string | null
}

export type Category = {
  name: string
  component: string
  count: number
}

export type NormalizedPass = {
  icon_link: string
  number?: string
  model?: string
  person?: string
  mainId: string
  date: string
  id: string
  type:
    | QuickFilterComponent.PermanentPassAuto
    | QuickFilterComponent.PermanentPassMen
}
